/**
* Hreflang cluster helpers for the NAATI pillar family and per-service
* mirrors. Every leaf in a cluster advertises every sibling so Google
* consolidates ranking signals across the 7 locales.
*/
const BASE = "https://thainotarylaw.com";

export const NAATI_HREFLANG_LINKS = [
  { rel: "alternate", hrefLang: "th", href: `${BASE}/naati` },
  { rel: "alternate", hrefLang: "en", href: `${BASE}/en/naati` },
  { rel: "alternate", hrefLang: "zh", href: `${BASE}/zh/naati` },
  { rel: "alternate", hrefLang: "ja", href: `${BASE}/ja/naati` },
  { rel: "alternate", hrefLang: "ko", href: `${BASE}/ko/naati` },
  { rel: "alternate", hrefLang: "vi", href: `${BASE}/vi/naati` },
  { rel: "alternate", hrefLang: "id", href: `${BASE}/id/naati` },
  { rel: "alternate", hrefLang: "x-default", href: `${BASE}/naati` },
] as const;

/**
* Build a hreflang cluster for a /naati/services/{slug} leaf and all
* 6 locale mirrors at /{locale}/naati/services/{slug}. Thai canonical
* is used as x-default so the Thai page collects generic queries.
*/
export function naatiServiceHreflangLinks(slug: string) {
  const svc = `/naati/services/${slug}`;
  return [
    { rel: "alternate", hrefLang: "th", href: `${BASE}${svc}` },
    { rel: "alternate", hrefLang: "en", href: `${BASE}/en${svc}` },
    { rel: "alternate", hrefLang: "zh", href: `${BASE}/zh${svc}` },
    { rel: "alternate", hrefLang: "ja", href: `${BASE}/ja${svc}` },
    { rel: "alternate", hrefLang: "ko", href: `${BASE}/ko${svc}` },
    { rel: "alternate", hrefLang: "vi", href: `${BASE}/vi${svc}` },
    { rel: "alternate", hrefLang: "id", href: `${BASE}/id${svc}` },
    { rel: "alternate", hrefLang: "x-default", href: `${BASE}${svc}` },
  ] as const;
}

/**
* Hreflang cluster for the NAATI FAQ hub: Thai canonical at /naati/questions
* plus the 6 localized mirrors at /{locale}/naati/questions.
*/
export const NAATI_QUESTIONS_HREFLANG_LINKS = [
  { rel: "alternate", hrefLang: "th", href: `${BASE}/naati/questions` },
  { rel: "alternate", hrefLang: "en", href: `${BASE}/en/naati/questions` },
  { rel: "alternate", hrefLang: "zh", href: `${BASE}/zh/naati/questions` },
  { rel: "alternate", hrefLang: "ja", href: `${BASE}/ja/naati/questions` },
  { rel: "alternate", hrefLang: "ko", href: `${BASE}/ko/naati/questions` },
  { rel: "alternate", hrefLang: "vi", href: `${BASE}/vi/naati/questions` },
  { rel: "alternate", hrefLang: "id", href: `${BASE}/id/naati/questions` },
  { rel: "alternate", hrefLang: "x-default", href: `${BASE}/naati/questions` },
] as const;

/**
* Locales that actually ship a physical /naati/compare mirror. Never advertise
* a locale in hreflang that would 404 — Google treats that as a broken cluster.
*/
export const NAATI_COMPARE_LOCALES = ["en", "zh", "ja", "ko", "vi", "id"] as const;

/** Reciprocal hreflang cluster for the compare hub (Thai = x-default). */
export function naatiCompareHubHreflangLinks() {
  return [
    { rel: "alternate", hrefLang: "th", href: `${BASE}/naati/compare` },
    ...NAATI_COMPARE_LOCALES.map((l) => ({
      rel: "alternate",
      hrefLang: l,
      href: `${BASE}/${l}/naati/compare`,
    })),
    { rel: "alternate", hrefLang: "x-default", href: `${BASE}/naati/compare` },
  ];
}

/** Reciprocal hreflang cluster for a single compare leaf. */
export function naatiCompareHreflangLinks(slug: string) {
  const p = `/naati/compare/${slug}`;
  return [
    { rel: "alternate", hrefLang: "th", href: `${BASE}${p}` },
    ...NAATI_COMPARE_LOCALES.map((l) => ({
      rel: "alternate",
      hrefLang: l,
      href: `${BASE}/${l}${p}`,
    })),
    { rel: "alternate", hrefLang: "x-default", href: `${BASE}${p}` },
  ];
}
